import { cn } from "@/lib/utils";
import type { FileRecord } from "@cohesive/api-client";
import { formatDistanceToNow } from "date-fns";
import { ExternalLink } from "lucide-react";
import React from "react";
import {
  FileRecordTypeIcon,
  formatFileSize,
  getFileNameFromPath,
  urlLabel,
} from "../../EntityCards/FileRecordCard";
import { FileRecordPreview } from "../../FileRecordPreview/FileRecordPreview";
import {
  WorkspaceCardFooterTitleRow,
  workspaceCardFooterTypeIconSizeClassName,
} from "../WorkspaceCardFooterTypeIcon";
import { WorkspaceCardStarButton } from "../WorkspaceCardStarButton";
import { WorkspaceGridCard } from "../WorkspaceGridCard";
import {
  gridCardFooterMetaRowClassName,
  gridCardFooterSubtitleClassName,
  gridCardFooterTitleClassName,
} from "../constants";

export interface WorkspaceFileCardProps {
  record: FileRecord;
  isSelected?: boolean;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  onDoubleClick?: React.MouseEventHandler<HTMLDivElement>;
  onContextMenu?: React.MouseEventHandler<HTMLDivElement>;
  /** Context menu rendered in the footer; shows on `group-hover`. */
  menuSlot?: React.ReactNode;
  /** Favorite state + toggle for the on-card corner star. Omit `onToggleStar`
   *  to hide the star (e.g. for non-favoritable file classes). */
  isFavorite?: boolean;
  onToggleStar?: (record: FileRecord) => void;
  /**
   * Optional render-prop wrapping just the thumbnail well — the workspace
   * grid passes a dwell-preview wrapper so a slow hover over the thumbnail
   * surfaces a larger preview without firing on the footer.
   */
  thumbnailWrapper?: (thumb: React.ReactNode) => React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

export const WorkspaceFileCard: React.FC<WorkspaceFileCardProps> = ({
  record,
  isSelected = false,
  onClick,
  onDoubleClick,
  onContextMenu,
  menuSlot,
  isFavorite = false,
  onToggleStar,
  thumbnailWrapper,
  className,
  style,
}) => {
  const displayTitle =
    (record.path && getFileNameFromPath(record.path)) || record.id || "File";

  const editedTime = record.updated_at
    ? formatDistanceToNow(new Date(record.updated_at), {
        addSuffix: true,
      }).replace(/^about /, "")
    : "";

  const hasSize = record.size != null && record.size > 0;

  const preview = (
    <>
      <FileRecordPreview
        contentType={record.content_type}
        originType={record.origin_type}
        url={record.url}
        previewUrls={record.preview_urls}
        status={record.status}
        alt={displayTitle}
        spinnerSize="lg"
        spinnerLabel
        className="absolute inset-0 h-full w-full"
      />
      {onToggleStar && (
        <WorkspaceCardStarButton
          isFavorite={isFavorite}
          onToggle={() => onToggleStar(record)}
        />
      )}
    </>
  );

  const footer = (
    <>
      <div className="min-w-0 flex-1">
        <WorkspaceCardFooterTitleRow
          icon={
            <FileRecordTypeIcon
              contentType={record.content_type || ""}
              originType={record.origin_type}
              className={workspaceCardFooterTypeIconSizeClassName}
            />
          }
        >
          {record.origin_url ? (
            <a
              href={record.origin_url}
              target="_blank"
              rel="noopener noreferrer"
              title={record.origin_url}
              onClick={(e) => e.stopPropagation()}
              className="group/title flex min-w-0 items-center gap-1"
            >
              <span
                className={cn(
                  gridCardFooterTitleClassName,
                  "underline-offset-2 group-hover/title:underline",
                )}
              >
                {urlLabel(record.origin_url)}
              </span>
              <ExternalLink className="h-3 w-3 shrink-0 text-neutral-400 opacity-0 transition-opacity group-hover/title:opacity-100" />
            </a>
          ) : (
            <p className={gridCardFooterTitleClassName}>{displayTitle}</p>
          )}
        </WorkspaceCardFooterTitleRow>

        {(editedTime || hasSize) && (
          <p
            className={cn(
              gridCardFooterMetaRowClassName,
              "truncate",
              gridCardFooterSubtitleClassName,
            )}
          >
            {editedTime}
            {editedTime && hasSize && " · "}
            {hasSize && formatFileSize(record.size)}
          </p>
        )}
      </div>

      {menuSlot && <div className="shrink-0">{menuSlot}</div>}
    </>
  );

  return (
    <WorkspaceGridCard
      aria-label={displayTitle}
      aria-selected={isSelected}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      onContextMenu={onContextMenu}
      preview={preview}
      footer={footer}
      thumbnailWrapper={thumbnailWrapper}
      isSelected={isSelected}
      interactive={onClick != null || onDoubleClick != null}
      className={className}
      style={style}
    />
  );
};
